import { z } from 'zod';
import { MasterErrorClass } from '../types/errors/shared';
/**
 * Public Experiences — the `PXE-0xx` error family.
 *
 * Follows the existing `MasterErrorClass` pattern exactly (message, code,
 * clientMessage, statusCode) and is spread into `ErrorUtility` in
 * `src/types/errors/types.ts`, so `handleErrors(error, reply)` serializes these
 * with the repository's existing envelope:
 *
 *   `{ errorCode: 'PXE-003', details?: unknown }`
 *
 * Every class takes `(message, clientMessage?)` so the existing
 * `handleErrorsWeb` helper — which instantiates every registered class with
 * `('', '')` — keeps working. An empty or omitted `clientMessage` falls back to
 * the class default (`||`, not `??`) so that helper never renders a blank toast.
 *
 * `clientMessage` is customer-safe copy. It never names a business, a provider
 * object, a capability or an amount.
 */
/** Stable machine-readable codes. Never renumbered. */
export var PublicExperienceErrorCodeEnum;
(function (PublicExperienceErrorCodeEnum) {
    PublicExperienceErrorCodeEnum["EXPERIENCE_NOT_FOUND"] = "PXE-001";
    PublicExperienceErrorCodeEnum["EXPERIENCE_DISABLED"] = "PXE-002";
    PublicExperienceErrorCodeEnum["OFFER_REVISION_STALE"] = "PXE-003";
    PublicExperienceErrorCodeEnum["ATTEMPT_CONFLICT"] = "PXE-004";
    PublicExperienceErrorCodeEnum["BUSINESS_NOT_ELIGIBLE"] = "PXE-005";
    PublicExperienceErrorCodeEnum["PROVIDER_UNSUPPORTED"] = "PXE-006";
    PublicExperienceErrorCodeEnum["CAPABILITY_INVALID"] = "PXE-007";
    PublicExperienceErrorCodeEnum["RECAPTCHA_FAILED"] = "PXE-008";
    PublicExperienceErrorCodeEnum["RECAPTCHA_UNAVAILABLE"] = "PXE-009";
    PublicExperienceErrorCodeEnum["DRAFT_REVISION_CONFLICT"] = "PXE-010";
    PublicExperienceErrorCodeEnum["CHECKOUT_NOT_FOUND"] = "PXE-011";
    PublicExperienceErrorCodeEnum["PROVIDER_UNAVAILABLE"] = "PXE-012";
    PublicExperienceErrorCodeEnum["FEATURE_DISABLED"] = "PXE-013";
    PublicExperienceErrorCodeEnum["INVALID_CONTACT_DATA"] = "PXE-014";
    // --- Part 2 (contract 2.0.0, direct charges) ---
    PublicExperienceErrorCodeEnum["CLIENT_UPGRADE_REQUIRED"] = "PXE-015";
    PublicExperienceErrorCodeEnum["OFFER_REPUBLISH_REQUIRED"] = "PXE-016";
    PublicExperienceErrorCodeEnum["ACCOUNT_NOT_DIRECT_ELIGIBLE"] = "PXE-017";
    PublicExperienceErrorCodeEnum["SCOPE_UNRESOLVED"] = "PXE-018";
    PublicExperienceErrorCodeEnum["SUBSCRIPTION_NOT_MANAGEABLE"] = "PXE-019";
    // Jotform extension (JF-1). Existing codes remain unchanged.
    PublicExperienceErrorCodeEnum["JOTFORM_INTEGRATION_UNAVAILABLE"] = "PXE-020";
    PublicExperienceErrorCodeEnum["JOTFORM_INTEGRATION_DISABLED"] = "PXE-021";
    PublicExperienceErrorCodeEnum["JOTFORM_REVISION_CONFLICT"] = "PXE-022";
    PublicExperienceErrorCodeEnum["JOTFORM_SETUP_INVALID"] = "PXE-023";
    PublicExperienceErrorCodeEnum["JOTFORM_VERIFICATION_UNAVAILABLE"] = "PXE-024";
    PublicExperienceErrorCodeEnum["JOTFORM_ASSOCIATION_CONFLICT"] = "PXE-025";
    PublicExperienceErrorCodeEnum["JOTFORM_CONTINUATION_INVALID"] = "PXE-026";
    PublicExperienceErrorCodeEnum["JOTFORM_RECOVERY_REQUIRED"] = "PXE-027";
    // Platform-managed Jotform webhooks (JF-WP1).
    PublicExperienceErrorCodeEnum["JOTFORM_WEBHOOK_MANAGEMENT_REQUIRED"] = "PXE-028";
    PublicExperienceErrorCodeEnum["JOTFORM_WEBHOOK_OPERATION_CONFLICT"] = "PXE-029";
    PublicExperienceErrorCodeEnum["JOTFORM_WEBHOOK_THROTTLED"] = "PXE-030";
    PublicExperienceErrorCodeEnum["JOTFORM_WEBHOOK_UNAVAILABLE"] = "PXE-031";
})(PublicExperienceErrorCodeEnum || (PublicExperienceErrorCodeEnum = {}));
/**
 * Why a connected account may not take direct charges (`PXE-017
 * details.reason`). The gate fails closed: there is no destination fallback.
 */
export var AccountNotDirectEligibleReasonEnum;
(function (AccountNotDirectEligibleReasonEnum) {
    /** No enabled online Stripe account is linked to the business. */
    AccountNotDirectEligibleReasonEnum["NOT_LINKED"] = "NOT_LINKED";
    /** `charges_enabled` is false or the `card_payments` capability is not active. */
    AccountNotDirectEligibleReasonEnum["CHARGES_DISABLED"] = "CHARGES_DISABLED";
    /** Legacy `type: 'custom'` / `application_custom` fee-payer configuration (DEC-P2-01). */
    AccountNotDirectEligibleReasonEnum["LEGACY_ACCOUNT_CONFIGURATION"] = "LEGACY_ACCOUNT_CONFIGURATION";
    /** The account is linked to more than one business and policy forbids sharing. */
    AccountNotDirectEligibleReasonEnum["ACCOUNT_SHARED"] = "ACCOUNT_SHARED";
    /** The `PUBLIC_EXPERIENCES_DIRECT_CHARGES_ENABLED` cutover flag is off (fail-closed). */
    AccountNotDirectEligibleReasonEnum["DIRECT_DISABLED"] = "DIRECT_DISABLED";
    /**
     * A POINTCRAFT_MANAGED_DIRECT account whose required payment-method domains
     * are not all registered and enabled (DEC-P2-18). Remediation: ensure
     * payment domains (`paymentDomains` on the eligibility DTO lists them).
     */
    AccountNotDirectEligibleReasonEnum["PAYMENT_DOMAINS_MISSING"] = "PAYMENT_DOMAINS_MISSING";
})(AccountNotDirectEligibleReasonEnum || (AccountNotDirectEligibleReasonEnum = {}));
export const AccountNotDirectEligibleReasonSchema = z.nativeEnum(AccountNotDirectEligibleReasonEnum);
export const PublicExperienceErrorCodeSchema = z.nativeEnum(PublicExperienceErrorCodeEnum);
/**
 * How a client should react to each code.
 *
 *  - `NO`            — the request will never succeed unchanged.
 *  - `AFTER_REFRESH` — re-read server state (offer / status) and resubmit.
 *  - `YES`           — a transient condition; retry with backoff.
 */
export var ErrorRetryabilityEnum;
(function (ErrorRetryabilityEnum) {
    ErrorRetryabilityEnum["NO"] = "NO";
    ErrorRetryabilityEnum["AFTER_REFRESH"] = "AFTER_REFRESH";
    ErrorRetryabilityEnum["YES"] = "YES";
})(ErrorRetryabilityEnum || (ErrorRetryabilityEnum = {}));
export const ErrorRetryabilitySchema = z.nativeEnum(ErrorRetryabilityEnum);
/**
 * Single source of truth for status codes and retryability. CONTRACTS.md §9 is
 * generated from this table by hand; the test suite asserts they stay in sync
 * with the error classes below.
 */
export const PUBLIC_EXPERIENCE_ERROR_CATALOG = {
    [PublicExperienceErrorCodeEnum.EXPERIENCE_NOT_FOUND]: {
        code: PublicExperienceErrorCodeEnum.EXPERIENCE_NOT_FOUND,
        utilityKey: 'PXE_EXPERIENCE_NOT_FOUND',
        httpStatus: 404,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'BOTH',
        description: 'No published experience resolves from the publicId, or the admin path businessId does not own the experienceId. Cross-tenant access deliberately returns this instead of 403 so experience ids cannot be enumerated.',
    },
    [PublicExperienceErrorCodeEnum.EXPERIENCE_DISABLED]: {
        code: PublicExperienceErrorCodeEnum.EXPERIENCE_DISABLED,
        utilityKey: 'PXE_EXPERIENCE_DISABLED',
        httpStatus: 409,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'PUBLIC',
        description: 'Returned by checkout CREATE only. The experience exists but is DISABLED, so no new checkout may start — the block is immediate. Checkouts already created keep working: their provider sessions may still be completed within the <=60 min session expiry, GET status continues to serve them, and webhooks still confirm them. Existing subscriptions are untouched.',
    },
    [PublicExperienceErrorCodeEnum.OFFER_REVISION_STALE]: {
        code: PublicExperienceErrorCodeEnum.OFFER_REVISION_STALE,
        utilityKey: 'PXE_OFFER_REVISION_STALE',
        httpStatus: 409,
        retryability: ErrorRetryabilityEnum.AFTER_REFRESH,
        surface: 'PUBLIC',
        description: 'The submitted offerRevision is not the currently published one. The offer is never silently re-priced: the client must re-fetch the offer and ask the customer to review and consent again.',
    },
    [PublicExperienceErrorCodeEnum.ATTEMPT_CONFLICT]: {
        code: PublicExperienceErrorCodeEnum.ATTEMPT_CONFLICT,
        utilityKey: 'PXE_ATTEMPT_CONFLICT',
        httpStatus: 409,
        retryability: ErrorRetryabilityEnum.AFTER_REFRESH,
        surface: 'PUBLIC',
        description: 'The attemptKey already exists with a different request payload. The client must generate a new attemptKey to start a genuinely new attempt; replaying the identical payload returns the original checkout instead of this error.',
    },
    [PublicExperienceErrorCodeEnum.BUSINESS_NOT_ELIGIBLE]: {
        code: PublicExperienceErrorCodeEnum.BUSINESS_NOT_ELIGIBLE,
        utilityKey: 'PXE_BUSINESS_NOT_ELIGIBLE',
        httpStatus: 422,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'BOTH',
        description: 'The business has no enabled online payment account, or the connected account cannot process payments (canProcessPayments / charges_enabled false).',
    },
    [PublicExperienceErrorCodeEnum.PROVIDER_UNSUPPORTED]: {
        code: PublicExperienceErrorCodeEnum.PROVIDER_UNSUPPORTED,
        utilityKey: 'PXE_PROVIDER_UNSUPPORTED',
        httpStatus: 422,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'BOTH',
        description: "The business's online provider is not Stripe. MVP1 is Stripe-only; Tilled and other providers are rejected here rather than half-supported.",
    },
    [PublicExperienceErrorCodeEnum.CAPABILITY_INVALID]: {
        code: PublicExperienceErrorCodeEnum.CAPABILITY_INVALID,
        utilityKey: 'PXE_CAPABILITY_INVALID',
        httpStatus: 401,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'PUBLIC',
        description: 'The X-Checkout-Capability header is missing, malformed, expired or does not match the stored digest. Returned identically for all four cases so it cannot be used as an oracle. A valid checkoutId alone is never sufficient.',
    },
    [PublicExperienceErrorCodeEnum.RECAPTCHA_FAILED]: {
        code: PublicExperienceErrorCodeEnum.RECAPTCHA_FAILED,
        utilityKey: 'PXE_RECAPTCHA_FAILED',
        httpStatus: 400,
        retryability: ErrorRetryabilityEnum.AFTER_REFRESH,
        surface: 'PUBLIC',
        description: 'The x-recaptcha-token header is missing, or the assessment rejected the token. The client must mint a fresh token before retrying.',
    },
    [PublicExperienceErrorCodeEnum.RECAPTCHA_UNAVAILABLE]: {
        code: PublicExperienceErrorCodeEnum.RECAPTCHA_UNAVAILABLE,
        utilityKey: 'PXE_RECAPTCHA_UNAVAILABLE',
        httpStatus: 503,
        retryability: ErrorRetryabilityEnum.YES,
        surface: 'PUBLIC',
        description: 'The reCAPTCHA Enterprise assessment could not be performed (service or IAM failure). Fails closed: no checkout is created.',
    },
    [PublicExperienceErrorCodeEnum.DRAFT_REVISION_CONFLICT]: {
        code: PublicExperienceErrorCodeEnum.DRAFT_REVISION_CONFLICT,
        utilityKey: 'PXE_DRAFT_REVISION_CONFLICT',
        httpStatus: 409,
        retryability: ErrorRetryabilityEnum.AFTER_REFRESH,
        surface: 'ADMIN',
        description: 'expectedDraftRevision does not match the stored draftRevision: another admin saved first. The client must re-read the experience and re-apply its edits.',
    },
    [PublicExperienceErrorCodeEnum.CHECKOUT_NOT_FOUND]: {
        code: PublicExperienceErrorCodeEnum.CHECKOUT_NOT_FOUND,
        utilityKey: 'PXE_CHECKOUT_NOT_FOUND',
        httpStatus: 404,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'ADMIN',
        description: 'Admin-only: no checkout document for the given checkoutId under this business. The public status route returns PXE-007 CAPABILITY_INVALID instead, so a guest cannot probe for existence.',
    },
    [PublicExperienceErrorCodeEnum.PROVIDER_UNAVAILABLE]: {
        code: PublicExperienceErrorCodeEnum.PROVIDER_UNAVAILABLE,
        utilityKey: 'PXE_PROVIDER_UNAVAILABLE',
        httpStatus: 503,
        retryability: ErrorRetryabilityEnum.YES,
        surface: 'BOTH',
        description: 'The payment provider could not be reached or returned a retryable error. The local checkout claim survives, so retrying with the SAME attemptKey resumes it rather than creating a second subscription.',
    },
    [PublicExperienceErrorCodeEnum.FEATURE_DISABLED]: {
        code: PublicExperienceErrorCodeEnum.FEATURE_DISABLED,
        utilityKey: 'PXE_FEATURE_DISABLED',
        httpStatus: 404,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'BOTH',
        description: 'The PUBLIC_EXPERIENCES_FEAT_ENABLED Remote Config flag is off (read fail-closed). 404 rather than 403 so a disabled feature does not advertise itself. Webhook processing is NOT gated by this flag.',
    },
    [PublicExperienceErrorCodeEnum.INVALID_CONTACT_DATA]: {
        code: PublicExperienceErrorCodeEnum.INVALID_CONTACT_DATA,
        utilityKey: 'PXE_INVALID_CONTACT_DATA',
        httpStatus: 400,
        retryability: ErrorRetryabilityEnum.AFTER_REFRESH,
        surface: 'PUBLIC',
        description: 'One or more contact fields or the consent flag failed validation. details.fieldErrors carries stable dot-notation paths (contact.email, consent.accepted, ...) so the UI can attach errors to controls.',
    },
    [PublicExperienceErrorCodeEnum.CLIENT_UPGRADE_REQUIRED]: {
        code: PublicExperienceErrorCodeEnum.CLIENT_UPGRADE_REQUIRED,
        utilityKey: 'PXE_CLIENT_UPGRADE_REQUIRED',
        httpStatus: 409,
        retryability: ErrorRetryabilityEnum.AFTER_REFRESH,
        surface: 'PUBLIC',
        description: 'The experience is published with a DIRECT linkage but the client did not declare x-pxe-client-contract >= 2. Returned BEFORE the claim transaction and before any provider write, so a stale client never receives a session it cannot mount. details.requiredClientContract names the minimum. The client must reload to pick up the current build.',
    },
    [PublicExperienceErrorCodeEnum.OFFER_REPUBLISH_REQUIRED]: {
        code: PublicExperienceErrorCodeEnum.OFFER_REPUBLISH_REQUIRED,
        utilityKey: 'PXE_OFFER_REPUBLISH_REQUIRED',
        httpStatus: 409,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'BOTH',
        description: 'The published linkage is a schemaVersion 1 (platform-owned, destination) catalog, or a v1 checkout was replayed or resumed after the direct-charge cutover. Nothing is rerouted: the business must republish the experience under direct charges before new checkouts can start.',
    },
    [PublicExperienceErrorCodeEnum.ACCOUNT_NOT_DIRECT_ELIGIBLE]: {
        code: PublicExperienceErrorCodeEnum.ACCOUNT_NOT_DIRECT_ELIGIBLE,
        utilityKey: 'PXE_ACCOUNT_NOT_DIRECT_ELIGIBLE',
        httpStatus: 422,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'BOTH',
        description: 'The direct-eligibility gate refused the connected account (fail closed, no destination fallback). details.reason is one of NOT_LINKED, CHARGES_DISABLED, LEGACY_ACCOUNT_CONFIGURATION, ACCOUNT_SHARED, DIRECT_DISABLED. Emitted on publish (admin and merchant) and re-checked on checkout create.',
    },
    [PublicExperienceErrorCodeEnum.SCOPE_UNRESOLVED]: {
        code: PublicExperienceErrorCodeEnum.SCOPE_UNRESOLVED,
        utilityKey: 'PXE_SCOPE_UNRESOLVED',
        httpStatus: 500,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'BOTH',
        description: 'Operator recovery: a schemaVersion 2 record has no parseable executionScope, or the stored scope contradicts the record (e.g. a DIRECT scope whose merchant and object accounts differ). The server never guesses an account and never falls back to the platform; the record is left untouched for an operator.',
    },
    [PublicExperienceErrorCodeEnum.SUBSCRIPTION_NOT_MANAGEABLE]: {
        code: PublicExperienceErrorCodeEnum.SUBSCRIPTION_NOT_MANAGEABLE,
        utilityKey: 'PXE_SUBSCRIPTION_NOT_MANAGEABLE',
        httpStatus: 409,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'ADMIN',
        description: 'Cancel was requested for a subscription PointCraft did not create in the current execution scope (manageable === false): not linked to a submission, created under another scope, or not owned by the resolved account. Nothing is changed at the provider.',
    },
    [PublicExperienceErrorCodeEnum.JOTFORM_INTEGRATION_UNAVAILABLE]: {
        code: PublicExperienceErrorCodeEnum.JOTFORM_INTEGRATION_UNAVAILABLE,
        utilityKey: 'PXE_JOTFORM_INTEGRATION_UNAVAILABLE',
        httpStatus: 404,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'BOTH',
        description: 'The Jotform integration is unavailable or inaccessible in this scope. Return the same response for missing or unauthorized bindings.',
    },
    [PublicExperienceErrorCodeEnum.JOTFORM_INTEGRATION_DISABLED]: {
        code: PublicExperienceErrorCodeEnum.JOTFORM_INTEGRATION_DISABLED,
        utilityKey: 'PXE_JOTFORM_INTEGRATION_DISABLED',
        httpStatus: 409,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'PUBLIC',
        description: 'The binding is disabled for new reservations or financial claims. Existing accepted attempts retain authorized status, linkage and payment recovery.',
    },
    [PublicExperienceErrorCodeEnum.JOTFORM_REVISION_CONFLICT]: {
        code: PublicExperienceErrorCodeEnum.JOTFORM_REVISION_CONFLICT,
        utilityKey: 'PXE_JOTFORM_REVISION_CONFLICT',
        httpStatus: 409,
        retryability: ErrorRetryabilityEnum.AFTER_REFRESH,
        surface: 'BOTH',
        description: 'The binding or mapping revision changed. Re-read configuration and review unclaimed work; never rotate or replace a financially claimed attempt.',
    },
    [PublicExperienceErrorCodeEnum.JOTFORM_SETUP_INVALID]: {
        code: PublicExperienceErrorCodeEnum.JOTFORM_SETUP_INVALID,
        utilityKey: 'PXE_JOTFORM_SETUP_INVALID',
        httpStatus: 422,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'BOTH',
        description: 'The integration setup failed its form, redirect, mapping or payment-readiness checks. Remediate setup before starting new payments.',
    },
    [PublicExperienceErrorCodeEnum.JOTFORM_VERIFICATION_UNAVAILABLE]: {
        code: PublicExperienceErrorCodeEnum.JOTFORM_VERIFICATION_UNAVAILABLE,
        utilityKey: 'PXE_JOTFORM_VERIFICATION_UNAVAILABLE',
        httpStatus: 503,
        retryability: ErrorRetryabilityEnum.YES,
        surface: 'BOTH',
        description: 'External submission verification is temporarily unavailable. Retry verification with bounded backoff, preserving existing payment and any verified association.',
    },
    [PublicExperienceErrorCodeEnum.JOTFORM_ASSOCIATION_CONFLICT]: {
        code: PublicExperienceErrorCodeEnum.JOTFORM_ASSOCIATION_CONFLICT,
        utilityKey: 'PXE_JOTFORM_ASSOCIATION_CONFLICT',
        httpStatus: 409,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'BOTH',
        description: 'The external submission association conflicts with frozen evidence or an existing claim. Preserve established links and require authorized review; never rebill.',
    },
    [PublicExperienceErrorCodeEnum.JOTFORM_CONTINUATION_INVALID]: {
        code: PublicExperienceErrorCodeEnum.JOTFORM_CONTINUATION_INVALID,
        utilityKey: 'PXE_JOTFORM_CONTINUATION_INVALID',
        httpStatus: 401,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'PUBLIC',
        description: 'The continuation token, nonce or restricted popup grant is missing, expired, consumed or mismatched. Uniform denial; no checkout creation or capability escalation.',
    },
    [PublicExperienceErrorCodeEnum.JOTFORM_RECOVERY_REQUIRED]: {
        code: PublicExperienceErrorCodeEnum.JOTFORM_RECOVERY_REQUIRED,
        utilityKey: 'PXE_JOTFORM_RECOVERY_REQUIRED',
        httpStatus: 409,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'BOTH',
        description: 'The original attempt cannot safely continue with the available proof or provider evidence. Require recovery of that attempt without automatic retries or a replacement charge.',
    },
    [PublicExperienceErrorCodeEnum.JOTFORM_WEBHOOK_MANAGEMENT_REQUIRED]: {
        code: PublicExperienceErrorCodeEnum.JOTFORM_WEBHOOK_MANAGEMENT_REQUIRED,
        utilityKey: 'PXE_JOTFORM_WEBHOOK_MANAGEMENT_REQUIRED',
        httpStatus: 403,
        retryability: ErrorRetryabilityEnum.NO,
        surface: 'ADMIN',
        description: 'Webhook installation or rotation requires an explicitly authorized form management grant. Local security revocation does not require provider write permission.',
    },
    [PublicExperienceErrorCodeEnum.JOTFORM_WEBHOOK_OPERATION_CONFLICT]: {
        code: PublicExperienceErrorCodeEnum.JOTFORM_WEBHOOK_OPERATION_CONFLICT,
        utilityKey: 'PXE_JOTFORM_WEBHOOK_OPERATION_CONFLICT',
        httpStatus: 409,
        retryability: ErrorRetryabilityEnum.AFTER_REFRESH,
        surface: 'ADMIN',
        description: 'The selected binding or installation revision changed, another rotation is still in grace, or the request ID was reused with different content. Read the current installation before submitting an authorized operation.',
    },
    [PublicExperienceErrorCodeEnum.JOTFORM_WEBHOOK_THROTTLED]: {
        code: PublicExperienceErrorCodeEnum.JOTFORM_WEBHOOK_THROTTLED,
        utilityKey: 'PXE_JOTFORM_WEBHOOK_THROTTLED',
        httpStatus: 429,
        retryability: ErrorRetryabilityEnum.YES,
        surface: 'BOTH',
        description: 'Webhook processing is temporarily rate or capacity limited. Respect Retry-After and reuse the same management request ID for an identical retry; no new payment attempt is authorized.',
    },
    [PublicExperienceErrorCodeEnum.JOTFORM_WEBHOOK_UNAVAILABLE]: {
        code: PublicExperienceErrorCodeEnum.JOTFORM_WEBHOOK_UNAVAILABLE,
        utilityKey: 'PXE_JOTFORM_WEBHOOK_UNAVAILABLE',
        httpStatus: 503,
        retryability: ErrorRetryabilityEnum.YES,
        surface: 'BOTH',
        description: 'A webhook dependency is temporarily unavailable. Never acknowledge work that was not durably accepted or claim local revocation that did not commit. Management retries preserve the same request ID and payload.',
    },
};
/**
 * `ErrorUtility` entries. Keys are prefixed `PXE_` to stay collision-free inside
 * the flat `ErrorUtility` namespace.
 */
export const PublicExperienceErrorClasses = {
    PXE_EXPERIENCE_NOT_FOUND: class PXE_EXPERIENCE_NOT_FOUND extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'This page is no longer available.';
            super(message, PublicExperienceErrorCodeEnum.EXPERIENCE_NOT_FOUND, clientMessage || defaultClientMessage, 404);
            this.name = 'Public experience not found';
        }
    },
    PXE_EXPERIENCE_DISABLED: class PXE_EXPERIENCE_DISABLED extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'This form is not accepting new submissions right now.';
            super(message, PublicExperienceErrorCodeEnum.EXPERIENCE_DISABLED, clientMessage || defaultClientMessage, 409);
            this.name = 'Public experience disabled';
        }
    },
    PXE_OFFER_REVISION_STALE: class PXE_OFFER_REVISION_STALE extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'The details of this offer changed. Please review the updated amount before continuing.';
            super(message, PublicExperienceErrorCodeEnum.OFFER_REVISION_STALE, clientMessage || defaultClientMessage, 409);
            this.name = 'Offer revision stale';
        }
    },
    PXE_ATTEMPT_CONFLICT: class PXE_ATTEMPT_CONFLICT extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'We could not continue this attempt. Please start again.';
            super(message, PublicExperienceErrorCodeEnum.ATTEMPT_CONFLICT, clientMessage || defaultClientMessage, 409);
            this.name = 'Checkout attempt conflict';
        }
    },
    PXE_BUSINESS_NOT_ELIGIBLE: class PXE_BUSINESS_NOT_ELIGIBLE extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'Payments are not available for this page right now.';
            super(message, PublicExperienceErrorCodeEnum.BUSINESS_NOT_ELIGIBLE, clientMessage || defaultClientMessage, 422);
            this.name = 'Business not eligible for public experiences';
        }
    },
    PXE_PROVIDER_UNSUPPORTED: class PXE_PROVIDER_UNSUPPORTED extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'Payments are not available for this page right now.';
            super(message, PublicExperienceErrorCodeEnum.PROVIDER_UNSUPPORTED, clientMessage || defaultClientMessage, 422);
            this.name = 'Payment provider unsupported';
        }
    },
    PXE_CAPABILITY_INVALID: class PXE_CAPABILITY_INVALID extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'This checkout session is no longer available. Please start again.';
            super(message, PublicExperienceErrorCodeEnum.CAPABILITY_INVALID, clientMessage || defaultClientMessage, 401);
            this.name = 'Checkout capability invalid';
        }
    },
    PXE_RECAPTCHA_FAILED: class PXE_RECAPTCHA_FAILED extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'We could not verify this request. Please try again.';
            super(message, PublicExperienceErrorCodeEnum.RECAPTCHA_FAILED, clientMessage || defaultClientMessage, 400);
            this.name = 'reCAPTCHA verification failed';
        }
    },
    PXE_RECAPTCHA_UNAVAILABLE: class PXE_RECAPTCHA_UNAVAILABLE extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'We are unable to process this request right now. Please try again shortly.';
            super(message, PublicExperienceErrorCodeEnum.RECAPTCHA_UNAVAILABLE, clientMessage || defaultClientMessage, 503);
            this.name = 'reCAPTCHA unavailable';
        }
    },
    PXE_DRAFT_REVISION_CONFLICT: class PXE_DRAFT_REVISION_CONFLICT extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'Someone else saved changes to this experience. Reload and reapply your edits.';
            super(message, PublicExperienceErrorCodeEnum.DRAFT_REVISION_CONFLICT, clientMessage || defaultClientMessage, 409);
            this.name = 'Draft revision conflict';
        }
    },
    PXE_CHECKOUT_NOT_FOUND: class PXE_CHECKOUT_NOT_FOUND extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'Checkout not found.';
            super(message, PublicExperienceErrorCodeEnum.CHECKOUT_NOT_FOUND, clientMessage || defaultClientMessage, 404);
            this.name = 'Checkout not found';
        }
    },
    PXE_PROVIDER_UNAVAILABLE: class PXE_PROVIDER_UNAVAILABLE extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'Payments are temporarily unavailable. Please try again shortly.';
            super(message, PublicExperienceErrorCodeEnum.PROVIDER_UNAVAILABLE, clientMessage || defaultClientMessage, 503);
            this.name = 'Payment provider unavailable';
        }
    },
    PXE_FEATURE_DISABLED: class PXE_FEATURE_DISABLED extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'This page is no longer available.';
            super(message, PublicExperienceErrorCodeEnum.FEATURE_DISABLED, clientMessage || defaultClientMessage, 404);
            this.name = 'Public experiences feature disabled';
        }
    },
    PXE_INVALID_CONTACT_DATA: class PXE_INVALID_CONTACT_DATA extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'Please check the highlighted fields.';
            super(message, PublicExperienceErrorCodeEnum.INVALID_CONTACT_DATA, clientMessage || defaultClientMessage, 400);
            this.name = 'Invalid contact data';
        }
    },
    PXE_CLIENT_UPGRADE_REQUIRED: class PXE_CLIENT_UPGRADE_REQUIRED extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'This page has been updated. Please refresh to continue.';
            super(message, PublicExperienceErrorCodeEnum.CLIENT_UPGRADE_REQUIRED, clientMessage || defaultClientMessage, 409);
            this.name = 'Client upgrade required';
        }
    },
    PXE_OFFER_REPUBLISH_REQUIRED: class PXE_OFFER_REPUBLISH_REQUIRED extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'This form is being updated and cannot accept payments right now.';
            super(message, PublicExperienceErrorCodeEnum.OFFER_REPUBLISH_REQUIRED, clientMessage || defaultClientMessage, 409);
            this.name = 'Offer republish required';
        }
    },
    PXE_ACCOUNT_NOT_DIRECT_ELIGIBLE: class PXE_ACCOUNT_NOT_DIRECT_ELIGIBLE extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'Payments are not available for this page right now.';
            super(message, PublicExperienceErrorCodeEnum.ACCOUNT_NOT_DIRECT_ELIGIBLE, clientMessage || defaultClientMessage, 422);
            this.name = 'Account not eligible for direct charges';
        }
    },
    PXE_SCOPE_UNRESOLVED: class PXE_SCOPE_UNRESOLVED extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'We could not process this request. Please try again later.';
            super(message, PublicExperienceErrorCodeEnum.SCOPE_UNRESOLVED, clientMessage || defaultClientMessage, 500);
            this.name = 'Execution scope unresolved';
        }
    },
    PXE_SUBSCRIPTION_NOT_MANAGEABLE: class PXE_SUBSCRIPTION_NOT_MANAGEABLE extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'This recurring payment cannot be managed from here.';
            super(message, PublicExperienceErrorCodeEnum.SUBSCRIPTION_NOT_MANAGEABLE, clientMessage || defaultClientMessage, 409);
            this.name = 'Subscription not manageable';
        }
    },
    PXE_JOTFORM_INTEGRATION_UNAVAILABLE: class PXE_JOTFORM_INTEGRATION_UNAVAILABLE extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'This payment page is unavailable.';
            super(message, PublicExperienceErrorCodeEnum.JOTFORM_INTEGRATION_UNAVAILABLE, clientMessage || defaultClientMessage, 404);
            this.name = 'Jotform integration unavailable';
        }
    },
    PXE_JOTFORM_INTEGRATION_DISABLED: class PXE_JOTFORM_INTEGRATION_DISABLED extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'This form is not accepting new payments right now.';
            super(message, PublicExperienceErrorCodeEnum.JOTFORM_INTEGRATION_DISABLED, clientMessage || defaultClientMessage, 409);
            this.name = 'Jotform integration disabled';
        }
    },
    PXE_JOTFORM_REVISION_CONFLICT: class PXE_JOTFORM_REVISION_CONFLICT extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'The form setup changed. Review the updated details before continuing.';
            super(message, PublicExperienceErrorCodeEnum.JOTFORM_REVISION_CONFLICT, clientMessage || defaultClientMessage, 409);
            this.name = 'Jotform revision conflict';
        }
    },
    PXE_JOTFORM_SETUP_INVALID: class PXE_JOTFORM_SETUP_INVALID extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'This payment page needs setup before it can accept payments.';
            super(message, PublicExperienceErrorCodeEnum.JOTFORM_SETUP_INVALID, clientMessage || defaultClientMessage, 422);
            this.name = 'Jotform setup invalid';
        }
    },
    PXE_JOTFORM_VERIFICATION_UNAVAILABLE: class PXE_JOTFORM_VERIFICATION_UNAVAILABLE extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'Submission verification is temporarily unavailable. Your payment status is unchanged.';
            super(message, PublicExperienceErrorCodeEnum.JOTFORM_VERIFICATION_UNAVAILABLE, clientMessage || defaultClientMessage, 503);
            this.name = 'Jotform verification unavailable';
        }
    },
    PXE_JOTFORM_ASSOCIATION_CONFLICT: class PXE_JOTFORM_ASSOCIATION_CONFLICT extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'This submission needs review. Do not make another payment.';
            super(message, PublicExperienceErrorCodeEnum.JOTFORM_ASSOCIATION_CONFLICT, clientMessage || defaultClientMessage, 409);
            this.name = 'Jotform association conflict';
        }
    },
    PXE_JOTFORM_CONTINUATION_INVALID: class PXE_JOTFORM_CONTINUATION_INVALID extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'This payment window cannot continue. Return to the original form to resume.';
            super(message, PublicExperienceErrorCodeEnum.JOTFORM_CONTINUATION_INVALID, clientMessage || defaultClientMessage, 401);
            this.name = 'Jotform continuation invalid';
        }
    },
    PXE_JOTFORM_RECOVERY_REQUIRED: class PXE_JOTFORM_RECOVERY_REQUIRED extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'This payment needs recovery. Do not start another payment; return to the original form or contact support.';
            super(message, PublicExperienceErrorCodeEnum.JOTFORM_RECOVERY_REQUIRED, clientMessage || defaultClientMessage, 409);
            this.name = 'Jotform recovery required';
        }
    },
    PXE_JOTFORM_WEBHOOK_MANAGEMENT_REQUIRED: class PXE_JOTFORM_WEBHOOK_MANAGEMENT_REQUIRED extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'Webhook management permission is required to connect or rotate this webhook.';
            super(message, PublicExperienceErrorCodeEnum.JOTFORM_WEBHOOK_MANAGEMENT_REQUIRED, clientMessage || defaultClientMessage, 403);
            this.name = 'Jotform webhook management required';
        }
    },
    PXE_JOTFORM_WEBHOOK_OPERATION_CONFLICT: class PXE_JOTFORM_WEBHOOK_OPERATION_CONFLICT extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'Webhook setup changed or another operation is still active. Refresh its status before continuing.';
            super(message, PublicExperienceErrorCodeEnum.JOTFORM_WEBHOOK_OPERATION_CONFLICT, clientMessage || defaultClientMessage, 409);
            this.name = 'Jotform webhook operation conflict';
        }
    },
    PXE_JOTFORM_WEBHOOK_THROTTLED: class PXE_JOTFORM_WEBHOOK_THROTTLED extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'Webhook processing is busy. Wait before retrying the same request.';
            super(message, PublicExperienceErrorCodeEnum.JOTFORM_WEBHOOK_THROTTLED, clientMessage || defaultClientMessage, 429);
            this.name = 'Jotform webhook throttled';
        }
    },
    PXE_JOTFORM_WEBHOOK_UNAVAILABLE: class PXE_JOTFORM_WEBHOOK_UNAVAILABLE extends MasterErrorClass {
        constructor(message, clientMessage) {
            const defaultClientMessage = 'Webhook processing is temporarily unavailable. Try the same request again shortly.';
            super(message, PublicExperienceErrorCodeEnum.JOTFORM_WEBHOOK_UNAVAILABLE, clientMessage || defaultClientMessage, 503);
            this.name = 'Jotform webhook unavailable';
        }
    },
};
/**
 * The wire error envelope, as produced by the backend's existing
 * `handleErrors(error, reply)`: an `errorCode` plus optional `details`.
 *
 * This is NOT wrapped in `ResultSchema`/`AdminResultSchema` — those wrap success
 * payloads only. `details` is present only where this contract says so.
 */
export const PublicExperienceErrorEnvelopeSchema = z
    .object({
    errorCode: z.string().min(1).max(32),
    details: z.unknown().optional(),
})
    .strict();
/**
 * The `PXE-014 INVALID_CONTACT_DATA` envelope, whose `details` carries the
 * stable field-error list.
 */
export const PublicExperienceFieldErrorEnvelopeSchema = z
    .object({
    errorCode: z.literal(PublicExperienceErrorCodeEnum.INVALID_CONTACT_DATA),
    details: z
        .object({
        fieldErrors: z
            .array(z
            .object({
            path: z.string().min(1).max(200),
            code: z.string().min(1).max(64),
            message: z.string().min(1).max(500),
        })
            .strict())
            .min(1),
    })
        .strict(),
})
    .strict();
/** `PXE-015` envelope: names the minimum client contract the server requires. */
export const ClientUpgradeRequiredErrorEnvelopeSchema = z
    .object({
    errorCode: z.literal(PublicExperienceErrorCodeEnum.CLIENT_UPGRADE_REQUIRED),
    details: z
        .object({ requiredClientContract: z.number().int().min(1) })
        .strict(),
})
    .strict();
/** `PXE-017` envelope: the gate's reason, and nothing else (no account id). */
export const AccountNotDirectEligibleErrorEnvelopeSchema = z
    .object({
    errorCode: z.literal(PublicExperienceErrorCodeEnum.ACCOUNT_NOT_DIRECT_ELIGIBLE),
    details: z
        .object({ reason: AccountNotDirectEligibleReasonSchema })
        .strict(),
})
    .strict();
